import { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

type PeriodDays = 7 | 30 | 90;

type UserStats = {
  total_users: number;
  new_users: number;
  active_users: number;
  total_leaders: number;
};

type PlatformStats = {
  total_clubs: number;
  active_runs: number;
  total_follows: number;
  rsvps_in_period: number;
  going_in_period: number;
  interested_in_period: number;
};

type TopClub = {
  id: string;
  name: string;
  rsvp_count: number;
  unique_rsvpers: number;
  followers: number;
};

type SignupRow = {
  id: string;
  display_name: string | null;
  username: string | null;
  created_at: string;
};

function StatCard({
  label,
  value,
  sublabel,
  loading,
}: {
  label: string;
  value: number | null;
  sublabel?: string;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="animate-pulse rounded-2xl border border-black/10 bg-black/5 h-28" />
    );
  }
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-black/40">
        {label}
      </p>
      <p className="mt-3 text-3xl font-black text-black">
        {value?.toLocaleString() ?? '—'}
      </p>
      {sublabel && (
        <p className="mt-1 text-xs text-black/45">{sublabel}</p>
      )}
    </div>
  );
}

function SectionHeading({ title }: { title: string }) {
  return (
    <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-black/40">
      {title}
    </h2>
  );
}

type OwnershipClaim = {
  id: string;
  club_id: string;
  leader_id: string | null;
  user_id: string | null;
  status: string;
  created_at: string;
  clubs: { name: string; slug: string } | null;
  run_leaders: { name: string; email: string } | null;
  profiles: { display_name: string | null; email: string | null } | null;
};

function OwnershipClaimsPanel() {
  const [claims, setClaims] = useState<OwnershipClaim[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    const { data: rawClaims } = await supabase
      .from('club_ownership_claims')
      .select('id, club_id, leader_id, user_id, status, created_at, clubs(name, slug), run_leaders(name, email)')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    const claimsArr = (rawClaims as unknown as Omit<OwnershipClaim, 'profiles'>[]) || [];

    // Enrich with profiles for non-leader claimants
    const userIds = claimsArr
      .filter((c) => c.user_id && !c.leader_id)
      .map((c) => c.user_id as string);

    let profileMap: Record<string, { display_name: string | null; email: string | null }> = {};
    if (userIds.length > 0) {
      const { data: profileRows } = await supabase
        .from('profiles')
        .select('id, display_name, email')
        .in('id', userIds);
      for (const p of profileRows || []) {
        profileMap[p.id] = { display_name: p.display_name, email: p.email };
      }
    }

    const enriched: OwnershipClaim[] = claimsArr.map((c) => ({
      ...c,
      profiles: c.user_id ? (profileMap[c.user_id] ?? null) : null,
    }));
    setClaims(enriched);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  async function approve(claim: OwnershipClaim) {
    // Admin-only RPC: promotes the claimant to a run_leader if they aren't one,
    // attaches the club and marks the claim approved in a single transaction.
    // RLS deliberately blocks doing any of this from the client.
    const { error } = await supabase.rpc('approve_club_ownership_claim', {
      p_claim_id: claim.id,
    });
    if (error) {
      console.error('Failed to approve club ownership claim:', error.message);
    }
    fetchClaims();
  }

  async function reject(claim: OwnershipClaim) {
    const { error } = await supabase.rpc('reject_club_ownership_claim', {
      p_claim_id: claim.id,
    });
    if (error) {
      console.error('Failed to reject club ownership claim:', error.message);
    }
    fetchClaims();
  }

  return (
    <div className="space-y-3">
      <SectionHeading title="Club Ownership Claims" />
      <div className="overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm">
        {loading ? (
          <div className="animate-pulse space-y-px">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-14 bg-black/5" />
            ))}
          </div>
        ) : claims.length > 0 ? (
          <ul className="divide-y divide-black/5">
            {claims.map((claim) => (
              <li key={claim.id} className="flex items-center justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-black">
                    {claim.clubs?.name ?? claim.club_id}
                  </p>
                  <p className="truncate text-xs text-black/50">
                    {claim.run_leaders?.name ?? claim.profiles?.display_name ?? 'New user'}
                    {(claim.run_leaders?.email || claim.profiles?.email) && (
                      <> &middot; {claim.run_leaders?.email || claim.profiles?.email}</>
                    )}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => approve(claim)}
                    className="rounded-full bg-black px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-black/80"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => reject(claim)}
                    className="rounded-full border border-black/10 px-3 py-1.5 text-xs font-semibold text-black/60 transition hover:bg-black/5"
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-5 py-10 text-center text-sm text-black/40">
            No pending ownership claims.
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { leader, loading: authLoading } = useAuth();

  const [period, setPeriod] = useState<PeriodDays>(30);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);
  const [topClubs, setTopClubs] = useState<TopClub[] | null>(null);
  const [recentSignups, setRecentSignups] = useState<SignupRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!leader?.is_admin) return;

    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      setUserStats(null);
      setPlatformStats(null);
      setTopClubs(null);
      setRecentSignups(null);

      try {
        const [userRes, platformRes, clubsRes, signupsRes] = await Promise.all([
          supabase.rpc('get_admin_user_stats', { days_back: period }),
          supabase.rpc('get_admin_platform_stats', { days_back: period }),
          supabase.rpc('get_admin_top_clubs', { days_back: period }),
          supabase.rpc('get_admin_recent_signups', { days_back: period }),
        ]);

        if (userRes.error) throw new Error(userRes.error.message);
        if (platformRes.error) throw new Error(platformRes.error.message);

        setUserStats(userRes.data as UserStats);
        setPlatformStats(platformRes.data as PlatformStats);
        setTopClubs((clubsRes.data ?? []) as TopClub[]);
        setRecentSignups((signupsRes.data ?? []) as SignupRow[]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load stats.');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [period, leader]);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-black border-t-transparent" />
      </div>
    );
  }

  if (!leader?.is_admin) {
    return <Navigate to="/" replace />;
  }

  const periodLabel = `last ${period} days`;

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-20">
      <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6">
        <div className="space-y-8">

          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-black/40">
                Admin
              </p>
              <h1 className="mt-1 text-3xl font-black tracking-tight text-black sm:text-4xl">
                Dashboard
              </h1>
            </div>

            {/* Period toggle */}
            <div className="flex rounded-2xl border border-black/10 bg-white p-1 shadow-sm">
              {([7, 30, 90] as PeriodDays[]).map((d) => (
                <button
                  key={d}
                  onClick={() => setPeriod(d)}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                    period === d
                      ? 'bg-black text-white'
                      : 'text-black/50 hover:text-black'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Users section */}
          <div className="space-y-3">
            <SectionHeading title="Users" />
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard
                label="Registered Users"
                value={userStats?.total_users ?? null}
                loading={loading}
              />
              <StatCard
                label="New Users"
                value={userStats?.new_users ?? null}
                sublabel={periodLabel}
                loading={loading}
              />
              <StatCard
                label="Active Users"
                value={userStats?.active_users ?? null}
                sublabel={`RSVPed in ${periodLabel}`}
                loading={loading}
              />
              <StatCard
                label="Run Leaders"
                value={userStats?.total_leaders ?? null}
                loading={loading}
              />
            </div>
          </div>

          {/* Platform section */}
          <div className="space-y-3">
            <SectionHeading title="Platform" />
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard
                label="Total Clubs"
                value={platformStats?.total_clubs ?? null}
                loading={loading}
              />
              <StatCard
                label="Active Runs"
                value={platformStats?.active_runs ?? null}
                sublabel="Scheduled & live"
                loading={loading}
              />
              <StatCard
                label="RSVPs"
                value={platformStats?.rsvps_in_period ?? null}
                sublabel={periodLabel}
                loading={loading}
              />
              <StatCard
                label="Club Follows"
                value={platformStats?.total_follows ?? null}
                sublabel="All time"
                loading={loading}
              />
            </div>

            {/* RSVP breakdown */}
            {!loading && platformStats && (
              <div className="flex gap-3">
                <div className="flex-1 rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600">
                    Going
                  </p>
                  <p className="mt-1 text-2xl font-black text-emerald-700">
                    {platformStats.going_in_period.toLocaleString()}
                  </p>
                </div>
                <div className="flex-1 rounded-2xl border border-yellow-100 bg-yellow-50 px-5 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-yellow-700">
                    Interested
                  </p>
                  <p className="mt-1 text-2xl font-black text-yellow-800">
                    {platformStats.interested_in_period.toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Visitor analytics callout */}
          <div className="rounded-2xl border border-black/10 bg-white px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 text-lg">📊</span>
              <div>
                <p className="text-sm font-semibold text-black">
                  Visitor analytics
                </p>
                <p className="mt-0.5 text-sm text-black/55">
                  Page views and visitor counts are tracked via Vercel Analytics — not stored in Supabase.
                  View them in your{' '}
                  <a
                    href="https://vercel.com/analytics"
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-2 hover:text-black"
                  >
                    Vercel dashboard ↗
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>

          {/* Club engagement table */}
          <div className="space-y-3">
            <SectionHeading title={`Club Engagement — ${periodLabel}`} />
            <div className="overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm">
              {loading ? (
                <div className="animate-pulse space-y-px">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-14 bg-black/5" />
                  ))}
                </div>
              ) : topClubs && topClubs.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-black/8 bg-black/2">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-black/40">
                        #
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-black/40">
                        Club
                      </th>
                      <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-black/40">
                        RSVPs
                      </th>
                      <th className="hidden px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-black/40 sm:table-cell">
                        Unique RSVPers
                      </th>
                      <th className="hidden px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-black/40 sm:table-cell">
                        Followers
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5">
                    {topClubs.map((club, i) => (
                      <tr key={club.id} className="hover:bg-black/2 transition">
                        <td className="px-5 py-3.5 text-black/30 tabular-nums">
                          {i + 1}
                        </td>
                        <td className="px-5 py-3.5 font-medium text-black">
                          <Link
                            to={`/clubs/${club.id}`}
                            className="hover:underline underline-offset-2"
                          >
                            {club.name}
                          </Link>
                        </td>
                        <td className="px-5 py-3.5 text-right tabular-nums text-black">
                          {club.rsvp_count.toLocaleString()}
                        </td>
                        <td className="hidden px-5 py-3.5 text-right tabular-nums text-black/60 sm:table-cell">
                          {club.unique_rsvpers.toLocaleString()}
                        </td>
                        <td className="hidden px-5 py-3.5 text-right tabular-nums text-black/60 sm:table-cell">
                          {club.followers.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="px-5 py-10 text-center text-sm text-black/40">
                  No RSVP activity in the {periodLabel}.
                </div>
              )}
            </div>
          </div>

          {/* Ownership claims */}
          <div className="max-w-2xl">
            <OwnershipClaimsPanel />
          </div>

          {/* Recent signups */}
          <div className="space-y-3">
            <SectionHeading title={`New Signups — ${periodLabel}`} />
            <div className="overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm">
              {loading ? (
                <div className="animate-pulse space-y-px">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-12 bg-black/5" />
                  ))}
                </div>
              ) : recentSignups && recentSignups.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-black/8 bg-black/2">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-black/40">
                        Name
                      </th>
                      <th className="hidden px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-black/40 sm:table-cell">
                        Username
                      </th>
                      <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.12em] text-black/40">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5">
                    {recentSignups.map((user) => (
                      <tr key={user.id} className="hover:bg-black/2 transition">
                        <td className="px-5 py-3 font-medium text-black">
                          {user.display_name || '—'}
                        </td>
                        <td className="hidden px-5 py-3 text-black/50 sm:table-cell">
                          {user.username ? `@${user.username}` : '—'}
                        </td>
                        <td className="px-5 py-3 text-right text-black/40 tabular-nums">
                          {new Date(user.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="px-5 py-10 text-center text-sm text-black/40">
                  No new signups in the {periodLabel}.
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </main>
  );
}
